/** UI helpers — solicitudes formales de licencia (Comercial 2B). */
import { canInstallLicenseForWorkspace, isSaasCommercialBuyer } from "@/lib/accountStatus";
import { resolveModuleCatalog, isEntitlementActive } from "@/modules/catalogModulos";
import {
  defaultRequestedModulesMap,
  formatModulesLabelFromDict,
  hasAtLeastOneCatalogModule,
  moduleIdsForLicenseRequest,
  moduleIdsForSaasCenter,
  moduleIdsForTrial,
  moduleLabel,
  resolveCommercialModuleCatalog,
} from "@/lib/commercialModuleCatalog";
import {
  defaultRequestedLicenseType,
  licenseTypeLabel,
} from "@/lib/licenseTypes";
import {
  DEFAULT_ESTIMATED_CLIENTS,
  DEFAULT_ESTIMATED_CLIENTS_RANGE,
  DEFAULT_ESTIMATED_USERS,
  DEFAULT_ESTIMATED_USERS_RANGE,
  normalizeScopeFormFields,
} from "@/lib/commercialLicenseScope";
import {
  DEFAULT_INVOICE_CFDI_USE,
  DEFAULT_INVOICE_PAYMENT_METHOD,
  invoiceCfdiUseLabelForPayload,
  invoicePaymentMethodLabelForPayload,
} from "@/lib/commercialInvoiceCatalog";

export const LICENSE_REQUEST_STATUS_PENDING = "pending";
export const LICENSE_REQUEST_STATUS_IN_REVIEW = "in_review";
export const LICENSE_REQUEST_STATUS_APPROVED = "approved";
export const LICENSE_REQUEST_STATUS_REJECTED = "rejected";
export const LICENSE_REQUEST_STATUS_ISSUED = "issued";

export const LICENSE_REQUEST_ACTIVE_STATUSES = new Set([
  LICENSE_REQUEST_STATUS_PENDING,
  LICENSE_REQUEST_STATUS_IN_REVIEW,
]);

export const LICENSE_REQUEST_STATUS_LABELS = {
  [LICENSE_REQUEST_STATUS_PENDING]: "Pendiente",
  [LICENSE_REQUEST_STATUS_IN_REVIEW]: "En revisión",
  [LICENSE_REQUEST_STATUS_APPROVED]: "Aprobada",
  [LICENSE_REQUEST_STATUS_REJECTED]: "Rechazada",
  [LICENSE_REQUEST_STATUS_ISSUED]: "Licencia emitida",
};

export const SECURITY_RECOVERY_QUESTION_PRESETS = [
  "¿En qué ciudad se fundó el negocio?",
  "¿Cuál es el nombre corto o comercial del negocio?",
  "¿Cuál es el segundo apellido del titular o responsable?",
  "¿Cuál fue el primer cliente del despacho?",
  "¿Cuál fue el primer RFC registrado en el sistema?",
  "¿Cuál es el nombre de una mascota o referencia personal?",
];

/** @param {{ q?: string, answer?: string }[]} rows */
export function validateSecurityRecoveryRows(rows = []) {
  if (!Array.isArray(rows) || rows.length !== 3) {
    return "Seleccione las 3 preguntas y capture las 3 respuestas.";
  }
  const questions = rows.map((r) => String(r?.q || "").trim());
  const answers = rows.map((r) => String(r?.answer || "").trim());
  if (questions.some((q) => !q) || answers.some((a) => !a)) {
    return "Seleccione las 3 preguntas y capture las 3 respuestas.";
  }
  if (new Set(questions).size !== 3) {
    return "Seleccione 3 preguntas distintas.";
  }
  return null;
}

/** @param {{ q?: string, answer?: string }[]} rows @param {number} rowIndex */
export function securityRecoveryOptionsForRow(rows = [], rowIndex = 0) {
  const selectedElsewhere = new Set(
    rows.map((r, i) => (i !== rowIndex ? String(r?.q || "").trim() : "")).filter(Boolean),
  );
  const current = String(rows[rowIndex]?.q || "").trim();
  const fromCatalog = SECURITY_RECOVERY_QUESTION_PRESETS.filter(
    (q) => !selectedElsewhere.has(q) || q === current,
  );
  if (current && !fromCatalog.includes(current)) {
    return [current, ...fromCatalog];
  }
  return fromCatalog;
}

export const INVOICE_STATUS_LABELS = {
  not_applicable: "No aplica",
  pending: "Pendiente de facturar",
  incomplete: "Datos incompletos",
  completed: "Factura realizada",
};

/** @param {object} [systemInfo] */
export function hasActiveCommercialLicense(systemInfo = {}) {
  const lic = systemInfo?.license;
  if (!lic?.present) return false;
  return ["active", "expiring_soon"].includes(String(lic?.status || ""));
}

/** @param {object} [systemInfo] */
export function resolveActiveModuleIds(systemInfo = {}) {
  if (!systemInfo) return [];
  const catalog = resolveCommercialModuleCatalog(systemInfo);
  const requestIds = moduleIdsForLicenseRequest(catalog);
  const perms = systemInfo.module_permissions || {};
  const ctx = {
    entitlements: systemInfo.entitlements || perms.entitlements,
    modulePermissions: perms,
  };
  return requestIds.filter((id) => isEntitlementActive(id, ctx));
}

/** @param {object} [systemInfo] */
export function resolveRequestableModuleIds(systemInfo = {}) {
  const catalog = resolveCommercialModuleCatalog(systemInfo);
  const active = new Set(resolveActiveModuleIds(systemInfo));
  return moduleIdsForLicenseRequest(catalog).filter((id) => !active.has(id));
}

/** @param {object} profile */
export function hasBuyerProfileBasics(profile = {}) {
  return Boolean((profile.rfc || "").trim() && (profile.email || profile.correo_facturacion || "").trim());
}

/** @param {object} form */
export function validateInvoiceFields(form = {}) {
  if (!form.requires_invoice) return null;
  const missing = [];
  if (!(form.invoice_rfc || "").trim()) missing.push("RFC");
  if (!(form.invoice_razon_social || "").trim()) missing.push("nombre o razón social");
  if (!(form.invoice_regimen_fiscal || "").trim()) missing.push("régimen fiscal");
  if (!(form.invoice_codigo_postal || "").trim()) missing.push("código postal fiscal");
  if (!(form.invoice_fiscal_address || "").trim()) missing.push("domicilio fiscal");
  if (!(form.invoice_email || "").trim()) missing.push("correo para factura");
  if (!(form.invoice_cfdi_use || "").trim()) missing.push("uso de CFDI");
  if (!(form.invoice_payment_method || "").trim()) missing.push("forma de pago");
  if (missing.length) {
    return `Complete los datos para factura: ${missing.join(", ")}.`;
  }
  return null;
}

/** @param {string} status */
export function invoiceStatusLabel(status) {
  return INVOICE_STATUS_LABELS[status] || status || "—";
}

/** @param {boolean} requiresInvoice */
export function formatRequiresInvoiceLabel(requiresInvoice) {
  return requiresInvoice ? "Sí" : "No";
}

export const LICENSE_REQUEST_MSG_ACTIVE =
  "Ya tienes una solicitud pendiente o en revisión.";

export const LICENSE_REQUEST_MSG_COOLDOWN =
  "Tu solicitud fue enviada correctamente. El equipo BNT la revisará y te contactará. Para evitar duplicados, podrás enviar una nueva solicitud después de 72 horas si aún no recibes respuesta.";

export const LICENSE_REQUEST_MSG_ACTIVE_LICENSE =
  "Ya tienes una licencia activa. Para ampliar módulos, usuarios o clientes, contacta a BNT "
  + "o solicita una actualización (renovación o ampliación).";

export const LICENSE_REQUEST_MSG_PDF_REGISTERED =
  "Solicitud PDF/manual registrada. BNT la revisará en el Centro SaaS.";

export const LICENSE_REQUEST_MSG_REMINDER_SENT =
  "Aviso de seguimiento enviado a BNT.";

export const LICENSE_REQUEST_MSG_REMINDER_TOO_SOON =
  "Ya se envió un aviso de seguimiento recientemente. El equipo BNT tiene registrada tu solicitud.";

export const LICENSE_REQUEST_CHANNEL_LABELS = {
  web: "Solicitud web",
  pdf_manual: "Solicitud PDF / manual",
};

export const LICENSE_REQUEST_MSG_ACTIVE_409 =
  "Ya tienes una solicitud pendiente de revisión.";

export const LICENSE_REQUEST_MSG_FORBIDDEN =
  "No tienes permiso para enviar solicitudes comerciales.";

export const LICENSE_REQUEST_MSG_NO_MODULE =
  "Selecciona al menos un módulo.";

export const LICENSE_REQUEST_MSG_RFC_REQUIRED =
  "El RFC es obligatorio para enviar la solicitud.";

export const LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED =
  "Debe aceptar primero los términos y condiciones antes de enviar la solicitud.";

export const LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED =
  "No fue posible cargar los documentos legales vigentes. Intenta de nuevo.";

export const LICENSE_REQUEST_MSG_LEGAL_ACCEPTANCE_FAILED =
  "No pudimos registrar la aceptación legal. Intenta nuevamente.";

export const LICENSE_REQUEST_MSG_GENERIC =
  "No pudimos enviar la solicitud. Intenta nuevamente.";

export const LICENSE_REQUEST_MSG_SUCCESS =
  "Solicitud web enviada correctamente. BNT revisará tu solicitud y te dará seguimiento.";

export const LICENSE_REQUEST_MSG_APPROVED =
  "Tu solicitud fue aprobada. BNT generará o actualizará tu licencia.";

/** @param {object} [user] @param {{ buyerPreviewUx?: boolean }} [opts] */
export function canManageCommercialLicenseRequests(user = {}, opts = {}) {
  if (!user || typeof user !== "object") return false;
  if (
    !opts.buyerPreviewUx
    && (user.is_platform_designer || user.role === "platform_designer")
    && !isSaasCommercialBuyer(user)
  ) {
    return false;
  }
  if (user.is_buyer_tenant && user.role === "buyer_admin") return true;
  return canInstallLicenseForWorkspace(user);
}

/**
 * ¿Mostrar UI de solicitud comercial en Configuración (comprador)?
 * @param {object} user
 * @param {{ workspaceMode?: string, canSwitch?: boolean }} [ctx]
 */
export function canShowCommercialLicenseRequestUi(user = {}, ctx = {}) {
  if (!canManageCommercialLicenseRequests(user, { buyerPreviewUx: ctx.buyerPreviewUx })) return false;
  const mode = String(ctx.workspaceMode || "despacho").toLowerCase();
  const canSwitch = Boolean(ctx.canSwitch);
  if (canSwitch && mode === "distribuidor" && user.can_generate_licenses) return false;
  return true;
}

/** Sesión emisor/distribuidor (no confundir con titular comprador en modo Despacho). */
export function isDistributorEmisorWorkspace(user = {}, ctx = {}) {
  const mode = String(ctx.workspaceMode || "despacho").toLowerCase();
  return Boolean(ctx.canSwitch && mode === "distribuidor" && user?.can_generate_licenses);
}

/**
 * Shell limpio comprador SaaS: oculta acordeón técnico, SAT diagnose y meta routing.
 * En modo Despacho aplica a titular/comprador aunque dev tenga ALLOW_LICENSE_GENERATION;
 * solo se excluye sesión distribuidor emisor, diseñador (sin vista previa comprador) o backdoor.
 * @param {object} user
 * @param {{ workspaceMode?: string, canSwitch?: boolean, isEmisorHub?: boolean, isPlatformDesigner?: boolean, buyerPreviewActive?: boolean, entryMode?: string|null }} [ctx]
 */
export function shouldUseBuyerCommercialCleanShell(user = {}, ctx = {}) {
  if (ctx.isEmisorHub || ctx.entryMode === "emisor") return false;
  if (user?.is_designer_backdoor) return false;
  if (ctx.isPlatformDesigner && !ctx.buyerPreviewActive) return false;
  if (isDistributorEmisorWorkspace(user, ctx)) return false;
  if (ctx.entryMode && ctx.entryMode !== "despacho") return false;
  return canManageCommercialLicenseRequests(user);
}

/** @param {object[]} items */
export function hasActiveRequest(items = []) {
  return (items || []).some((row) => LICENSE_REQUEST_ACTIVE_STATUSES.has(String(row?.status || "")));
}

/** @param {object[]} items */
export function findLatestApprovedRequest(items = []) {
  return (items || []).find((row) => row?.status === LICENSE_REQUEST_STATUS_APPROVED) || null;
}

/** @param {string} channel */
export function requestChannelLabel(channel) {
  return LICENSE_REQUEST_CHANNEL_LABELS[channel] || channel || "—";
}

/** @param {string} isoEnd */
export function formatCooldownRemaining(isoEnd) {
  if (!isoEnd) return "—";
  const end = new Date(isoEnd);
  if (Number.isNaN(end.getTime())) return "—";
  const diffMs = end.getTime() - Date.now();
  if (diffMs <= 0) return "disponible ahora";
  const hours = Math.ceil(diffMs / (1000 * 60 * 60));
  if (hours >= 48) {
    const days = Math.ceil(hours / 24);
    return `aprox. ${days} día${days === 1 ? "" : "s"}`;
  }
  return `aprox. ${hours} hora${hours === 1 ? "" : "s"}`;
}

/** @param {object} [gate] */
export function isSubmissionBlocked(gate) {
  return Boolean(gate?.blocked);
}

/** @param {string} status */
export function licenseRequestStatusLabel(status) {
  return LICENSE_REQUEST_STATUS_LABELS[status] || status || "—";
}

/** @param {Record<string, boolean>} modules @param {object[]} [catalog] */
export function formatRequestedModulesLabel(modules = {}, catalog) {
  const resolved = catalog || resolveModuleCatalog();
  return formatModulesLabelFromDict(modules, resolved);
}

/** @param {object} [systemInfo] @param {object} [profileDefaults] */
export function buildLicenseRequestFormDefaults(systemInfo = {}, profileDefaults = {}) {
  const catalog = resolveCommercialModuleCatalog(systemInfo);
  const profile = profileDefaults?.rfc ? profileDefaults : systemInfo?.profile || profileDefaults || {};
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  return {
    email: (profile.email || profile.correo_facturacion || "").trim(),
    rfc: (profile.rfc || "").trim().toUpperCase(),
    razon_social: (profile.razon_social || "").trim(),
    nombre_comercial: (profile.nombre_comercial || "").trim(),
    telefono: (profile.telefono || "").trim(),
    estimated_users: DEFAULT_ESTIMATED_USERS,
    estimated_clients: DEFAULT_ESTIMATED_CLIENTS,
    estimated_users_range: DEFAULT_ESTIMATED_USERS_RANGE,
    estimated_clients_range: DEFAULT_ESTIMATED_CLIENTS_RANGE,
    comments: "",
    wants_own_accounting: false,
    requested_license_type: defaultRequestedLicenseType(hasActiveLicense),
    requires_invoice: false,
    invoice_rfc: "",
    invoice_razon_social: "",
    invoice_regimen_fiscal: "",
    invoice_codigo_postal: "",
    invoice_fiscal_address: (profile.domicilio || "").trim(),
    invoice_email: "",
    invoice_cfdi_use: DEFAULT_INVOICE_CFDI_USE,
    invoice_payment_method: DEFAULT_INVOICE_PAYMENT_METHOD,
    use_registration_invoice_data: false,
    requested_modules: defaultRequestedModulesMap(catalog),
    commercial_plan: "basic",
    billing_cadence: "annual",
    payment_proof_file_id: "",
    payment_proof_filename: "",
    payment_comment: "",
  };
}

/** @param {Record<string, boolean>} modules @param {object} [systemInfo] @param {string} [commercialPlan] */
export function hasAtLeastOneRequestedModule(modules = {}, systemInfo = {}, commercialPlan = "") {
  if (commercialPlan && ["basic", "professional"].includes(String(commercialPlan).toLowerCase())) {
    return true;
  }
  const catalog = resolveCommercialModuleCatalog(systemInfo);
  return hasAtLeastOneCatalogModule(modules, catalog);
}

export const COMMERCIAL_PLAN_LABELS = {
  basic: "Básico",
  professional: "Profesional",
};

/** @param {string} planId */
export function commercialPlanLabel(planId) {
  return COMMERCIAL_PLAN_LABELS[String(planId || "").toLowerCase()] || planId || "—";
}

/** @param {{ legalAccepted?: boolean, legalCatalogLoaded?: boolean, activeBlocked?: boolean }} ctx */
export function canSubmitLicenseRequestWithLegal(ctx = {}) {
  if (ctx.activeBlocked) return false;
  if (!ctx.legalCatalogLoaded) return false;
  return Boolean(ctx.legalAccepted);
}

/** @param {object} form @param {object} [user] @param {object} [options] */
export function buildAgreementAcceptancePayload(form, user = {}, options = {}) {
  const catalog = options.catalog || resolveModuleCatalog();
  const requestIds = moduleIdsForLicenseRequest(catalog);
  const modules = {};
  const activeIds = options.activeModuleIds || [];
  const includeActiveForLegal = Boolean(options.includeActiveForLegal);
  requestIds.forEach((id) => {
    if (form.requested_modules?.[id]) modules[id] = true;
    else if (includeActiveForLegal && activeIds.includes(id)) modules[id] = true;
  });
  if (!Object.values(modules).some(Boolean) && activeIds.length) {
    activeIds.forEach((id) => {
      modules[id] = true;
    });
  }
  return {
    context: "license_request",
    modules_accepted: modules,
    request_snapshot: {
      rfc: (form.rfc || "").trim().toUpperCase(),
      razon_social: (form.razon_social || "").trim(),
      email: (form.email || user.email || "").trim(),
      telefono: (form.telefono || "").trim(),
    },
  };
}

/** @param {Error|object} err */
export function parseLicenseRequestSubmitError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 409) {
    const code = typeof detail === "object" ? detail?.code : "";
    if (code === "COMMERCIAL_LICENSE_REQUEST_COOLDOWN") {
      return detail?.message || LICENSE_REQUEST_MSG_COOLDOWN;
    }
    if (code === "COMMERCIAL_ACTIVE_LICENSE") {
      return detail?.message || LICENSE_REQUEST_MSG_ACTIVE_LICENSE;
    }
    if (code === "COMMERCIAL_EXPANSION_DUPLICATE") {
      return detail?.message || "Ya tienes una solicitud de ampliación en revisión.";
    }
    if (code === "COMMERCIAL_EXPANSION_NO_LICENSE") {
      return detail?.message || "Necesitas una licencia activa para solicitar ampliación.";
    }
    return LICENSE_REQUEST_MSG_ACTIVE_409;
  }
  if (status === 403) return LICENSE_REQUEST_MSG_FORBIDDEN;
  if (status === 400) {
    const code = typeof detail === "object" ? detail?.code : "";
    if (code === "COMMERCIAL_AGREEMENT_REQUIRED") return LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED;
    if (code === "COMMERCIAL_AGREEMENT_VERSION_MISMATCH") {
      return "La versión del contrato aceptado no coincide con la vigente. Recarga la página e intenta de nuevo.";
    }
    if (code === "COMMERCIAL_AGREEMENT_MODULES_MISMATCH") {
      return "Los módulos aceptados legalmente no cubren todos los módulos solicitados.";
    }
    const text =
      typeof detail === "string"
        ? detail
        : detail?.message || detail?.detail || "";
    if (/módulo|modulo/i.test(String(text))) return LICENSE_REQUEST_MSG_NO_MODULE;
    if (/RFC|rfc/i.test(String(text))) return LICENSE_REQUEST_MSG_RFC_REQUIRED;
    if (/aceptaci/i.test(String(text))) return LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED;
  }
  return LICENSE_REQUEST_MSG_GENERIC;
}

/** @param {object[]} items */
export function resolveLicenseRequestPanelState(items = []) {
  const activeBlocked = hasActiveRequest(items);
  const latestApproved = findLatestApprovedRequest(items);
  return {
    submitDisabled: activeBlocked,
    showActiveBlock: activeBlocked,
    showApprovedNotice: !activeBlocked && Boolean(latestApproved),
    canSubmitNew: !activeBlocked,
  };
}

/** @param {object} form @param {object} [user] @param {string} [agreementAcceptanceId] @param {object} [options] */
export function buildLicenseRequestPayload(form, user = {}, agreementAcceptanceId = "", options = {}) {
  const catalog = options.catalog || resolveModuleCatalog();
  const requestIds = moduleIdsForLicenseRequest(catalog);
  const modules = {};
  requestIds.forEach((id) => {
    if (form.requested_modules?.[id]) modules[id] = true;
  });
  const hasActiveLicense = Boolean(options.hasActiveLicense);
  const requestableIds = options.requestableModuleIds || requestIds;
  const hasNewModules = requestableIds.some((id) => modules[id]);
  const scope = normalizeScopeFormFields(form);
  const payload = {
    email: (form.email || user.email || "").trim(),
    rfc: (form.rfc || "").trim().toUpperCase(),
    razon_social: (form.razon_social || "").trim(),
    nombre_comercial: (form.nombre_comercial || "").trim() || undefined,
    telefono: (form.telefono || "").trim() || undefined,
    requested_modules: modules,
    estimated_users: scope.estimated_users,
    estimated_clients: scope.estimated_clients,
    estimated_users_range: scope.estimated_users_range,
    estimated_clients_range: scope.estimated_clients_range,
    commercial_review_required: scope.commercial_review_required,
    special_quote_required: scope.special_quote_required,
    wants_own_accounting: Boolean(form.wants_own_accounting),
    comments: (form.comments || "").trim(),
    request_kind: (form.request_kind || "").trim()
      || (hasActiveLicense ? "renewal_or_expansion" : "initial"),
    requested_license_type: (
      form.requested_license_type || defaultRequestedLicenseType(hasActiveLicense)
    ).trim(),
    requires_invoice: Boolean(form.requires_invoice),
  };
  if (form.requires_invoice) {
    payload.invoice_rfc = (form.invoice_rfc || "").trim().toUpperCase();
    payload.invoice_razon_social = (form.invoice_razon_social || "").trim();
    payload.invoice_regimen_fiscal = (form.invoice_regimen_fiscal || "").trim();
    payload.invoice_codigo_postal = (form.invoice_codigo_postal || "").trim();
    payload.invoice_fiscal_address = (form.invoice_fiscal_address || "").trim();
    payload.invoice_email = (form.invoice_email || "").trim();
    payload.invoice_cfdi_use = (form.invoice_cfdi_use || DEFAULT_INVOICE_CFDI_USE).trim().toUpperCase();
    payload.invoice_payment_method = (form.invoice_payment_method || DEFAULT_INVOICE_PAYMENT_METHOD).trim();
    payload.invoice_cfdi_use_label = invoiceCfdiUseLabelForPayload(payload.invoice_cfdi_use);
    payload.invoice_payment_method_label = invoicePaymentMethodLabelForPayload(payload.invoice_payment_method);
    payload.invoice_comments = (form.invoice_comments || "").trim();
  }
  const paymentFileId = (form.payment_proof_file_id || "").trim();
  if (paymentFileId) {
    payload.payment_proof_file_id = paymentFileId;
  }
  const paymentComment = (form.payment_comment || "").trim();
  if (paymentComment) {
    payload.payment_comment = paymentComment;
  }
  if (agreementAcceptanceId) {
    payload.agreement_acceptance_id = agreementAcceptanceId;
  }
  if (form.requested_authorized_users != null && form.requested_authorized_users !== "") {
    payload.requested_authorized_users = Number(form.requested_authorized_users);
  }
  if (form.requested_authorized_clients != null && form.requested_authorized_clients !== "") {
    payload.requested_authorized_clients = Number(form.requested_authorized_clients);
  }
  const plan = String(form.commercial_plan || "").trim().toLowerCase();
  if (plan && ["basic", "professional"].includes(plan)) {
    payload.commercial_plan = plan;
    payload.billing_cadence = String(form.billing_cadence || "annual").trim().toLowerCase();
  }
  if (hasActiveLicense && !hasNewModules && !payload.comments) {
    payload._needsRenewalComment = true;
  }
  return payload;
}

/** @param {object} form @param {object} [user] @param {object} [options] */
export function buildLicenseRequestPdfPayload(form, user = {}, options = {}) {
  const agreementAcceptanceId = String(options.agreementAcceptanceId || "").trim();
  const payload = buildLicenseRequestPayload(form, user, agreementAcceptanceId, options);
  delete payload._needsRenewalComment;
  return payload;
}

export {
  moduleLabel,
  moduleIdsForLicenseRequest,
  moduleIdsForTrial,
  moduleIdsForSaasCenter,
  resolveCommercialModuleCatalog,
};
